import { useState } from 'react';
import {
  Alert,
  Autocomplete,
  Avatar,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  IconButton,
  MenuItem,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import { ClockCounterClockwiseIcon, HouseIcon, TrashIcon, UsersThreeIcon, XIcon } from '@phosphor-icons/react';

import { useDebouncedBlockContent } from '@/hooks/use-debounced-block-content';
import { useOrgSecurityGroups, usePageEditors } from '@/hooks/use-page-editors';
import { usePageVersions } from '@/hooks/use-page-versions';
import { useUnitMemberDirectory } from '@/hooks/use-unit-member-directory';
import type { Page, PageVersion } from '@/types/database';

type PageDraft = Pick<
  Page,
  'title' | 'slug' | 'seo_title' | 'seo_description' | 'show_in_nav' | 'is_published' | 'visibility'
>;

interface PageSettingsDialogProps {
  open: boolean;
  onClose: () => void;
  page: Page | null;
  canManage: boolean;
  onUpdatePage: (updates: Partial<PageDraft>) => void;
  onDeletePage: () => void;
  // Publishing (false -> true) goes through this instead of onUpdatePage
  // directly, so the parent can run the youth-PII preflight scan first
  // (see PublishScanDialog) — unpublishing is always safe and still goes
  // straight through onUpdatePage.
  onRequestPublish: () => void;
  // Exclusive across the whole site (only one page can be home), so this
  // goes through its own handler rather than onUpdatePage's per-page patch
  // — the parent has to unset the previous home page too.
  onSetHomePage: () => void;
}

export function PageSettingsDialog({
  open,
  onClose,
  page,
  canManage,
  onUpdatePage,
  onDeletePage,
  onRequestPublish,
  onSetHomePage,
}: PageSettingsDialogProps) {
  const [tab, setTab] = useState<'general' | 'seo' | 'access' | 'history' | 'advanced'>('general');

  if (!page) return null;

  return (
    <Dialog key={page.id} open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Page Settings
        <IconButton onClick={onClose} aria-label="Close">
          <XIcon size={20} />
        </IconButton>
      </DialogTitle>
      <Tabs value={tab} onChange={(_event, value) => setTab(value)} sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
        <Tab label="General" value="general" />
        <Tab label="SEO" value="seo" />
        <Tab label="Access" value="access" />
        <Tab label="History" value="history" />
        <Tab label="Advanced" value="advanced" />
      </Tabs>
      <DialogContent>
        {tab === 'general' ? (
          <GeneralTab
            page={page}
            canManage={canManage}
            onUpdatePage={onUpdatePage}
            onRequestPublish={onRequestPublish}
            onSetHomePage={onSetHomePage}
          />
        ) : null}
        {tab === 'seo' ? <SeoTab page={page} canManage={canManage} onUpdatePage={onUpdatePage} /> : null}
        {tab === 'access' ? <AccessTab page={page} canManage={canManage} /> : null}
        {tab === 'history' ? <HistoryTab page={page} canManage={canManage} /> : null}
        {tab === 'advanced' ? (
          <AdvancedTab canManage={canManage} onDeletePage={onDeletePage} />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function GeneralTab({
  page,
  canManage,
  onUpdatePage,
  onRequestPublish,
  onSetHomePage,
}: {
  page: Page;
  canManage: boolean;
  onUpdatePage: (updates: Partial<PageDraft>) => void;
  onRequestPublish: () => void;
  onSetHomePage: () => void;
}) {
  const [draft, update] = useDebouncedBlockContent<PageDraft>(page, onUpdatePage);

  return (
    <Stack spacing={2.5} sx={{ py: 1 }}>
      <TextField
        label="Page Title"
        value={draft.title}
        disabled={!canManage}
        onChange={(event) => update({ ...draft, title: event.target.value })}
        fullWidth
      />
      <TextField
        label="URL Slug"
        value={draft.slug}
        disabled={!canManage}
        onChange={(event) => update({ ...draft, slug: event.target.value })}
        fullWidth
      />
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          bgcolor: 'action.hover',
          borderRadius: 1.5,
          px: 2,
          py: 1.5,
        }}
      >
        <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', minWidth: 0 }}>
          <HouseIcon size={18} weight={page.is_home ? 'fill' : 'regular'} />
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              {page.is_home ? 'This is the home page' : 'Home page'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {page.is_home
                ? "Visitors land here when they open the site's main URL."
                : 'What visitors see at the site\'s main URL, instead of a page slug.'}
            </Typography>
          </Box>
        </Stack>
        {!page.is_home ? (
          <Button size="small" variant="outlined" disabled={!canManage} onClick={onSetHomePage} sx={{ flexShrink: 0 }}>
            Set as home page
          </Button>
        ) : null}
      </Box>
      <FormControlLabel
        control={
          <Switch
            checked={draft.show_in_nav}
            disabled={!canManage}
            onChange={(event) => update({ ...draft, show_in_nav: event.target.checked }, { immediate: true })}
          />
        }
        label="Show in navigation"
      />
      <FormControlLabel
        control={
          <Switch
            checked={draft.is_published}
            disabled={!canManage}
            onChange={(event) => {
              if (event.target.checked) {
                onRequestPublish();
              } else {
                update({ ...draft, is_published: false }, { immediate: true });
              }
            }}
          />
        }
        label="Published"
      />
      <TextField
        select
        label="Who can see this page"
        value={draft.visibility}
        disabled={!canManage}
        onChange={(event) => update({ ...draft, visibility: event.target.value as Page['visibility'] }, { immediate: true })}
        helperText={
          draft.visibility === 'internal'
            ? 'Only signed-in unit members can view this page, even once published.'
            : 'Anyone can view this page once it is published.'
        }
        fullWidth
      >
        <MenuItem value="public">Public</MenuItem>
        <MenuItem value="internal">Internal — unit members only</MenuItem>
      </TextField>
    </Stack>
  );
}

function SeoTab({ page, canManage, onUpdatePage }: { page: Page; canManage: boolean; onUpdatePage: (updates: Partial<PageDraft>) => void }) {
  const [draft, update] = useDebouncedBlockContent<PageDraft>(page, onUpdatePage);

  return (
    <Stack spacing={2.5} sx={{ py: 1 }}>
      <TextField
        label="SEO Title"
        value={draft.seo_title ?? ''}
        disabled={!canManage}
        onChange={(event) => update({ ...draft, seo_title: event.target.value || null })}
        fullWidth
      />
      <TextField
        label="SEO Description"
        value={draft.seo_description ?? ''}
        disabled={!canManage}
        onChange={(event) => update({ ...draft, seo_description: event.target.value || null })}
        multiline
        minRows={3}
        fullWidth
      />
    </Stack>
  );
}

function AccessTab({ page, canManage }: { page: Page; canManage: boolean }) {
  const { editors, loading, error, saving, addPersonEditor, addGroupEditor, removeEditor } = usePageEditors(page.id);
  const { directory, isLoading: directoryLoading } = useUnitMemberDirectory();
  const { groups, loading: groupsLoading } = useOrgSecurityGroups();

  const assignedIds = new Set(editors.map((editor) => editor.clerk_user_id).filter(Boolean));
  const assignedGroupIds = new Set(editors.map((editor) => editor.security_group_id).filter(Boolean));
  const candidates = Array.from(directory.values()).filter((member) => !assignedIds.has(member.userId));
  const groupCandidates = groups.filter((group) => !assignedGroupIds.has(group.id));
  const groupById = new Map(groups.map((group) => [group.id, group]));

  return (
    <Stack spacing={2} sx={{ py: 1 }}>
      <Typography variant="body2" color="text.secondary">
        Editors assigned here can manage this page&apos;s content — add photos, text, and other blocks — without needing full website
        management access. Assigning a security group instead of a person keeps access current automatically as its membership
        changes in Scouting Ops Hub — a good fit for a role that changes hands, like a den leader.
      </Typography>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {!canManage ? (
        <Alert severity="info">Only unit managers can change who has access to this page.</Alert>
      ) : (
        <Stack spacing={1.5}>
          <Autocomplete
            options={candidates}
            loading={directoryLoading}
            getOptionLabel={(option) => option.name}
            onChange={(_event, value) => {
              if (value) void addPersonEditor(value.userId);
            }}
            value={null}
            renderInput={(params) => <TextField {...params} label="Add a person" placeholder="Search unit members" />}
          />
          <Autocomplete
            options={groupCandidates}
            loading={groupsLoading}
            getOptionLabel={(option) => option.name}
            onChange={(_event, value) => {
              if (value) void addGroupEditor(value.id);
            }}
            value={null}
            renderInput={(params) => <TextField {...params} label="Add a security group" placeholder="Search security groups" />}
          />
        </Stack>
      )}

      <Stack spacing={1}>
        {loading ? (
          <Typography variant="body2" color="text.secondary">Loading…</Typography>
        ) : editors.length === 0 ? (
          <Typography variant="body2" color="text.secondary">No page-specific editors assigned.</Typography>
        ) : (
          editors.map((editor) => {
            const isGroup = Boolean(editor.security_group_id);
            const member = editor.clerk_user_id ? directory.get(editor.clerk_user_id) : undefined;
            const group = editor.security_group_id ? groupById.get(editor.security_group_id) : undefined;
            return (
              <Stack key={editor.id} direction="row" spacing={1.5} sx={{ alignItems: 'center' }}>
                {isGroup ? (
                  <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                    <UsersThreeIcon size={16} />
                  </Avatar>
                ) : (
                  <Avatar src={member?.imageUrl} sx={{ width: 32, height: 32 }}>
                    {member?.name?.[0] ?? '?'}
                  </Avatar>
                )}
                <Typography variant="body2" sx={{ flex: 1 }}>
                  {isGroup ? (group?.name ?? 'Security group') : (member?.name ?? editor.clerk_user_id)}
                </Typography>
                {canManage ? (
                  <IconButton size="small" color="error" disabled={saving} onClick={() => void removeEditor(editor.id)} aria-label="Remove editor">
                    <TrashIcon size={16} />
                  </IconButton>
                ) : null}
              </Stack>
            );
          })
        )}
      </Stack>
    </Stack>
  );
}

function HistoryTab({ page, canManage }: { page: Page; canManage: boolean }) {
  const { versions, loading, error, restoring, restore } = usePageVersions(page.id);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const handleRestoreClick = (version: PageVersion) => {
    if (confirmingId !== version.id) {
      setConfirmingId(version.id);
      return;
    }
    void restore(version).then(() => setConfirmingId(null));
  };

  return (
    <Stack spacing={2} sx={{ py: 1 }}>
      <Typography variant="body2" color="text.secondary">
        A version is saved automatically every time this page is published. Restoring replaces the page&apos;s current content — its
        title, slug, and SEO settings are left as they are now.
      </Typography>

      {error ? <Alert severity="error">{error}</Alert> : null}

      {loading ? (
        <Typography variant="body2" color="text.secondary">Loading…</Typography>
      ) : versions.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          No versions yet — one will be saved the next time this page is published.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {versions.map((version) => (
            <Stack
              key={version.id}
              direction="row"
              spacing={1.5}
              sx={{ alignItems: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 1.5, p: 1.5 }}
            >
              <ClockCounterClockwiseIcon size={18} />
              <Box sx={{ flex: 1 }}>
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {new Date(version.created_at).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                  })}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {version.blocks_snapshot.length} block{version.blocks_snapshot.length === 1 ? '' : 's'}
                </Typography>
              </Box>
              {canManage ? (
                <Stack direction="row" spacing={1}>
                  {confirmingId === version.id ? (
                    <Button size="small" onClick={() => setConfirmingId(null)}>
                      Cancel
                    </Button>
                  ) : null}
                  <Button
                    size="small"
                    variant={confirmingId === version.id ? 'contained' : 'outlined'}
                    color={confirmingId === version.id ? 'error' : 'primary'}
                    disabled={restoring}
                    onClick={() => handleRestoreClick(version)}
                  >
                    {confirmingId === version.id ? 'Confirm restore' : 'Restore'}
                  </Button>
                </Stack>
              ) : null}
            </Stack>
          ))}
        </Stack>
      )}
    </Stack>
  );
}

function AdvancedTab({ canManage, onDeletePage }: { canManage: boolean; onDeletePage: () => void }) {
  return (
    <Stack spacing={2} sx={{ py: 1 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>Danger zone</Typography>
      <Box>
        <Button
          variant="outlined"
          color="error"
          disabled={!canManage}
          onClick={onDeletePage}
        >
          Delete page
        </Button>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          Deleting the last page is allowed — visitors will see a &quot;site coming soon&quot; message until a page is
          published again.
        </Typography>
      </Box>
    </Stack>
  );
}
